import { BaseSupabaseService } from './BaseSupabaseService';
import { handleServiceError } from '../utils/errorHandler';
import { PaymentAccount, StudentPaymentAccount, PaymentHistory, PaymentSystem, PaginatedResponse } from '../types';
import { requestEdgeFunction } from './edgeFunctionClient';

// ── Junction table (must be defined first so PaymentAccountService can use it) ──

interface LinkStudentPaymentAccountResponse {
  student_payment_account_id: number;
}

class StudentPaymentAccountServiceClass extends BaseSupabaseService<StudentPaymentAccount> {
  constructor() {
    super('students_payment_accounts');
  }

  async findByStudentId(studentId: number): Promise<StudentPaymentAccount | null> {
    const results = await this.findAll({
      filters: [{ column: 'student_id', value: studentId }],
      limit: 1,
    });
    return results[0] || null;
  }

  async findByStudentAndPaymentAccount(
    studentId: number,
    paymentAccountId: number,
  ): Promise<StudentPaymentAccount | null> {
    const results = await this.findAll({
      filters: [
        { column: 'student_id', value: studentId },
        { column: 'payment_account_id', value: paymentAccountId },
      ],
      limit: 1,
    });
    return results[0] || null;
  }

  async linkToStudent(studentId: number, paymentAccountId: number): Promise<StudentPaymentAccount> {
    const existing = await this.findByStudentAndPaymentAccount(studentId, paymentAccountId);
    if (existing) return existing;

    try {
      const response = await requestEdgeFunction<{ data: LinkStudentPaymentAccountResponse }>('payment-gateway', {
        method: 'POST',
        body: {
          action: 'link-student-payment-account',
          student_id: studentId,
          payment_account_id: paymentAccountId,
        },
      });

      if (response.data?.student_payment_account_id) {
        return {
          id: response.data.student_payment_account_id,
          student_id: studentId,
          payment_account_id: paymentAccountId,
        } as StudentPaymentAccount;
      }
    } catch (error) {
      const duplicated = await this.findByStudentAndPaymentAccount(studentId, paymentAccountId);
      if (duplicated) {
        return duplicated;
      }

      handleServiceError(error, 'vincular conta de pagamento à aluna');
    }

    throw new Error('Não foi possível vincular conta de pagamento à aluna.');
  }

  async unlinkFromStudent(studentId: number): Promise<void> {
    const junction = await this.findByStudentId(studentId);
    if (junction) await this.delete(junction.id);
  }

  async findAllByStudentIdWithAccount(studentId: number): Promise<(StudentPaymentAccount & { payment_accounts: PaymentAccount })[]> {
    try {
      const response = await requestEdgeFunction<{ data: (StudentPaymentAccount & { payment_accounts: PaymentAccount })[] }>('payment-gateway', {
        method: 'POST',
        body: {
          action: 'list-student-payment-accounts-with-account',
          student_id: studentId,
        },
      });
      return response.data ?? [];
    } catch (error) {
      handleServiceError(error, 'buscar contas vinculadas');
      return [];
    }
  }
}

export const StudentPaymentAccountService = new StudentPaymentAccountServiceClass();

// ── Payment account ──────────────────────────────────────────────────────────

class PaymentAccountServiceClass extends BaseSupabaseService<PaymentAccount> {
  constructor() {
    super('payment_accounts');
  }

  async findByStudentId(studentId: number): Promise<PaymentAccount | null> {
    try {
      const response = await requestEdgeFunction<{ data: PaymentAccount | null }>('payment-gateway', {
        method: 'POST',
        body: {
          action: 'find-portal-payment-account-by-student',
          student_id: studentId,
        },
      });
      return response.data ?? null;
    } catch (error) {
      handleServiceError(error, 'buscar conta de pagamento');
      return null;
    }
  }

  async findOrCreateByExternalId(
    paymentSystem: PaymentSystem,
    externalId: string,
    data: Partial<PaymentAccount>
  ): Promise<PaymentAccount> {
    const response = await requestEdgeFunction<{ data: PaymentAccount }>('payment-gateway', {
      method: 'POST',
      body: {
        action: 'find-or-create-portal-payment-account',
        payment_system: paymentSystem,
        external_customer_id: externalId,
        record: data,
      },
    }).catch((error) => {
      handleServiceError(error, 'buscar ou criar conta de pagamento');
      throw error;
    });

    return response.data;
  }
}

export const PaymentAccountService = new PaymentAccountServiceClass();

export interface PaymentHistorySummary {
  totalPaid: number;
  paidCount: number;
  lastPaymentDate: string | null;
  totalsByCurrency: {
    BRL: number;
    USD: number;
  };
}

export type PaginatedPaymentHistoryResponse = PaginatedResponse<PaymentHistory> & {
  summary: PaymentHistorySummary;
};

// ── Payment history ──────────────────────────────────────────────────────────

class PaymentHistoryServiceClass extends BaseSupabaseService<PaymentHistory> {
  constructor() {
    super('payment_history');
  }

  async findBySubscriptionId(subscriptionId: number): Promise<PaymentHistory[]> {
    return this.findAll({
      filters: [{ column: 'student_subscription_id', value: subscriptionId }],
      orderBy: { column: 'external_created_date', ascending: false },
    });
  }

  async findByPaymentAccountId(paymentAccountId: number, subscriptionId?: number | null): Promise<PaymentHistory[]> {
    return this.findAll({
      filters: subscriptionId
        ? [{ column: 'student_subscription_id', value: subscriptionId }]
        : [{ column: 'payment_account_id', value: paymentAccountId }],
      orderBy: { column: 'external_created_date', ascending: false },
    });
  }

  async listPaginated(
    page: number,
    pageSize: number,
    paymentAccountId: number,
    filters: {
      subscriptionId?: number | null;
      search?: string;
      status?: string[];
      chargeType?: string[];
      dateStart?: string;
      dateEnd?: string;
    } = {},
  ): Promise<PaginatedPaymentHistoryResponse> {
    return requestEdgeFunction<PaginatedPaymentHistoryResponse>('list-payment-history', {
      method: 'GET',
      query: {
        page,
        pageSize,
        paymentAccountId,
        subscriptionId: filters.subscriptionId ?? undefined,
        search: filters.search,
        status: filters.status?.length ? filters.status : undefined,
        chargeType: filters.chargeType?.length ? filters.chargeType : undefined,
        dateStart: filters.dateStart,
        dateEnd: filters.dateEnd,
      },
    }).catch((error) => {
      handleServiceError(error, 'listar histórico');
      throw error;
    });
  }

  async upsertBatch(
    records: Array<{
      payment_account_id: number;
      external_payment_id: string;
      external_subscription_id?: string | null;
      value: number;
      status: string;
      external_created_date: string;
      due_date?: string | null;
      paid_at?: string | null;
      payment_link_url?: string | null;
      currency?: 'BRL' | 'USD' | null;
      student_subscription_id: number | null;
    }>,
  ): Promise<void> {
    if (records.length === 0) return;
    await Promise.all(records.map(async (record) => {
      await this.create(record as Partial<PaymentHistory>);
    })).catch((error) => {
      handleServiceError(error, 'importar histórico de pagamentos');
      throw error;
    });
  }
}

export const PaymentHistoryService = new PaymentHistoryServiceClass();